import { Component, Input, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { Cerere, FormDataGroup } from '../../cerere.interface';
import { formConfigurations } from './informatii-aditionale';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { CereriService } from '../../cereri.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-informatii-aditionale',
    templateUrl: './informatii-aditionale.component.html',
    styleUrls: ['./informatii-aditionale.component.scss'],
})
export class InformatiiAditionaleComponent implements OnChanges {
    @Input() cerere!: Cerere | null;
    @Output() infoUpdated: EventEmitter<void> = new EventEmitter();

    selectedInfoStatus = 'info';

    helper = helper;

    has_checkbox_details = false;
    has_form_data = false;

    selectedFormGroup: FormDataGroup | null = null;

    currentFormFields: Array<{
        label: string;
        value: string;
        type?: string;
        isCheckboxField?: boolean;
    }> = [];

    preparedCheckboxDetails: Array<{
        label: string;
        value: boolean;
        type: string;
        isCheckboxField?: boolean;
    }> = [];

    constructor(private cereriService: CereriService, public permissionsService: PermissionsService) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['cerere']) {
            let currentSlug = this.cerere?.slug || 'default-slug';

            if (
                currentSlug === 'emitere-cert-urb-retele-tehnico-edilitare' ||
                currentSlug === 'emitere-cert-urb-mijloace-publicitare'
            ) {
                currentSlug = 'emitere-certificat-urbanism'
            }

            this.currentFormFields = this.getCurrentFormFields();
            this.preparedCheckboxDetails = this.prepareCheckboxDetails(currentSlug);
            this.has_checkbox_details = this.hasValidData(
                this.cerere?.checkbox_details
            );
            this.has_form_data = !!this.cerere?.formData?.some(
                (group) => group.fields?.length > 0
            );
            this.selectedFormGroup = this.cerere?.formData?.find(
                (group) => group.fields?.length > 0
            ) ?? null;
        }
    }

    selectFormGroup(group: FormDataGroup): void {
        this.selectedFormGroup = group;
    }

    onInfoChange(event: MatButtonToggleChange) {
        this.selectedInfoStatus = event.value;
    }

    formatFormDataValue(value: any): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        if (typeof value === 'boolean') {
            return value ? 'Da' : 'Nu';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return value;
    }

    getCurrentFormFields(): Array<{
        label: string;
        value: string;
        type?: string;
        isCheckboxField?: boolean;
    }> {
        let currentSlug = this.cerere?.slug || 'default-slug';

        if (
            currentSlug === 'emitere-cert-urb-retele-tehnico-edilitare' ||
            currentSlug === 'emitere-cert-urb-mijloace-publicitare'
        ) {
            currentSlug = 'emitere-certificat-urbanism'
        }

        const allFields = formConfigurations[currentSlug] || [];

        // Mark checkbox fields
        return allFields.map((field) => {
            return {
                ...field,
                isCheckboxField: field.value.includes('.'),
            };
        });
    }

    prepareCheckboxDetails(slug: string): any[] {
        return this.currentFormFields
            .filter((field) => field.value.includes('.')) // Filter to include all checkbox fields
            .map((field) => {
                const value = this.getValueByPath(
                    this.cerere?.checkbox_details,
                    field.value
                );

                // Format value if it's a date
                if (field.type === 'date') {
                    return { ...field, value: this.formatDate(value) };
                }

                return { ...field, value };
            });
    }

    private formatDate(dateString: string): string {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    private getValueByPath(obj: any, path: string): any {
        return path
            .split('.')
            .reduce((o, key) => (o && o[key] !== undefined ? o[key] : null), obj);
    }

    // This function checks if we have values inside a property, we are using this to check if checkbox_details has values or it's an empty array

    private hasValidData(data: any): boolean {
        if (data && typeof data === 'object') {
            return Object.values(data).some((nestedObj) => {
                if (nestedObj && typeof nestedObj === 'object') {
                    return Object.values(nestedObj).some((value) => !!value);
                }
                return false;
            });
        }
        return false;
    }

    updateInformatiiCerere(fieldData: any) {
        console.log('fieldData', fieldData);
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: "O să actualizați valoarea câmpului selectat. Sunteți sigur?",
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText:
                this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText:
                this.helper.swalTextConfig.validationMessages.cancelButton,
            input: 'text', // Enable text input
            inputValue: this.cerere?.informatii[fieldData.value] ?? '',
            inputPlaceholder:
                "Completați valoarea dorita",
            inputValidator: (value) => {
                if (!value) {
                    return 'Este necesar completarea unui motiv!';
                }
                return null; // Return null if input is valid
            },
            showLoaderOnConfirm: true,
            preConfirm: (inputValue) => {
                return this.cereriService
                    .cerereUpdateMetadate(this.cerere?.id_cerere ?? 0, { key: fieldData.value, value: inputValue })
                    .toPromise()
                    .then((response) => {
                        return response;
                        // reset info on View
                    })
                    .catch((err) => {
                        let msg;
                        try {
                            msg = err.error.errors.message[0];
                        } catch (error) {
                            msg = helper.dialogConfig.generalMessages.error;
                        }
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: "Metadate actualizate cu succes!",
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.infoUpdated.emit();
                });
            }
        });
    }
}
